import axios from '@/http';

import { alertController } from '@ionic/vue';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import i18n from '@/plugins/i18n';
import useAppStorage from '@/composables/useAppStorage';
import useTracking from '@/composables/useTracking';
import useRequestToast from '@/composables/useRequestToast';

import {
  RECENTLY_PLAYED_MAX_COUNT,
  RECENTLY_PLAYED_PROGRESS_STORAGE_KEY,
  RECENTLY_PLAYED_STORAGE_KEY,
  TRACKING_PODCAST_VIEW,
} from '@/config/constants';

import {
  getArrayItemFromLocalStorage,
  getObjectItemFromLocalStorage,
  isObject,
  getCurrentUnix,
  getLatestTrackPositionByTimestamp,
} from '@/helpers/functions';

import { STREAM_USER_PROGRESS } from '@/config/api';

const { setKey } = useAppStorage();

const initializeStates = () => {
  return {
    recentlyPlayed: [],
    recentlyPlayedProgress: {},

    playerActive: false,
    playerCollapsed: false,

    currentCompany: {},
    currentCourse: {},
    currentSeries: {},

    playlist: [],
    playlistTrackStart: 0,
    playlistCurrentIndex: 0,

    seekPosition: null,

    trackInfo: {
      trackPlaying: false,
      trackDuration: 0,
      trackCurrentTime: 0,
    },

    streamSkipAutoplay: null,

    progressHistory: [],

    playerPlayPauseTriggers: {
      play: 0,
      pause: 0,
    },

    percentageListened: 0,
  };
};

export const state = () => initializeStates();

export const actions = {
  async openPlayer(
    { commit, state, dispatch, rootGetters },
    {
      company,
      course,
      series,
      playlist,
      startAt = 0,
      tracking = TRACKING_PODCAST_VIEW,
      skipAutoplay = false,
      collapse = false,
    },
  ) {
    // A paid podcast without a fulfilled payment cannot be played: the user first
    // has to pay on the website (payment_url), after which we reload the course.
    if (course?.payment_required && course?.payment_url) {
      dispatch('promptPodcastPayment', {
        company,
        course,
        series,
        playlist,
        startAt,
        tracking,
        skipAutoplay,
        collapse,
      });
      return;
    }

    try {
      // Paid podcasts can only be played after the payment is completed on the
      // website; the course details response flags this with payment_required.
      if (series?.payment_required || course?.payment_required) {
        const { openToast } = useRequestToast();
        openToast({
          color: 'warning',
          message: i18n.global.t('podcast.paymentRequired'),
        });
        return;
      }

      const { trackThis } = useTracking();
      trackThis(tracking, {}, course.streams[startAt]?.id);

      let newPlaylist = playlist;
      if (series) {
        newPlaylist = series.children.flatMap((season) =>
          season.children.flatMap((episode) => episode.streams),
        );

        // Sort by created_at ascending
        newPlaylist.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at),
        );

        commit('setSeriesData', series);
      } else {
        commit('setSeriesData', {});
      }

      const savedStreamId = course.streams[startAt]?.id;

      const isLogged = rootGetters['authentication/isLogged'];
      if (isLogged) {
        await dispatch('questionnaires/getAllForms', savedStreamId, {
          root: true,
        });
      }

      const newStartedAt = newPlaylist.findIndex(
        (stream) => stream.id === savedStreamId,
      );

      if (newStartedAt === -1) {
        return;
      }

      if (skipAutoplay === true) {
        commit('setSkipAutoplayId', savedStreamId);
      }

      commit('resetTrackInfo');
      commit('setPlaylist', {
        playlist: newPlaylist,
        startAt: newStartedAt,
      });
      commit('setCompanyData', company); // @todo remove this
      commit('setCourseData', course); // @todo accept this, make required

      commit('setPlayerState', true);
      commit('setPlayerCollapse', collapse);
      dispatch('addRecentlyPlayed', { course, startAt });
    } catch (error) {
      const { openToast } = useRequestToast();
      const responseData = error?.response?.data;

      // A 403 on the stream details means the podcast is not (yet) accessible
      // for this user, e.g. a paid podcast without a completed payment.
      if (error?.response?.status === 403) {
        openToast({
          color: 'warning',
          message: i18n.global.t('podcast.paymentRequired'),
        });
        return;
      }

      openToast({
        color: 'danger',
        message:
          (typeof responseData === 'string' ? responseData : responseData?.message) ||
          'Error when opening the player',
      });
    }
  },

  /**
   * Ask the user to pay for a paid podcast: opens the course payment page in the
   * in-app browser, reloads the course when the app resumes (or via the reload
   * button) and opens the player once the payment is fulfilled.
   */
  async promptPodcastPayment({ dispatch }, payload) {
    const t = (key) => i18n.global.t(key);
    const { openToast } = useRequestToast();
    const { course } = payload;

    let resumeListener = null;
    let finished = false;

    const cleanup = async () => {
      finished = true;
      if (resumeListener) {
        await resumeListener.remove();
        resumeListener = null;
      }
    };

    const reloadAndRetry = async () => {
      try {
        const freshCourse = await dispatch('course/setCourseData', course.id, {
          root: true,
        });

        if (freshCourse && !freshCourse.payment_required) {
          await cleanup();

          const topAlert = await alertController.getTop();
          if (topAlert) {
            await topAlert.dismiss();
          }

          dispatch('openPlayer', {
            ...payload,
            course: freshCourse,
            series: payload.series ? freshCourse : undefined,
            playlist: freshCourse.streams?.length
              ? freshCourse.streams
              : payload.playlist,
          });

          return true;
        }
      } catch (error) {
        // Keep the dialog open; the user can retry via the reload button.
      }

      return false;
    };

    const presentWaitingAlert = async () => {
      const waitingAlert = await alertController.create({
        header: t('paidPodcast.waitingTitle'),
        message: t('paidPodcast.waitingMessage'),
        backdropDismiss: false,
        buttons: [
          {
            text: t('paidPodcast.cancel'),
            role: 'cancel',
            handler: () => {
              cleanup();
            },
          },
          {
            text: t('paidPodcast.reload'),
            handler: async () => {
              const paid = await reloadAndRetry();
              if (!paid) {
                openToast({
                  color: 'warning',
                  message: t('paidPodcast.paymentNotFound'),
                });
              }
              return paid;
            },
          },
        ],
      });

      await waitingAlert.present();
    };

    const openPaymentPage = async () => {
      resumeListener = await App.addListener('resume', () => {
        if (!finished) {
          reloadAndRetry();
        }
      });

      await presentWaitingAlert();
      await Browser.open({ url: course.payment_url });
    };

    const paymentAlert = await alertController.create({
      header: t('paidPodcast.title'),
      message: t('paidPodcast.message'),
      buttons: [
        {
          text: t('paidPodcast.cancel'),
          role: 'cancel',
        },
        {
          text: t('paidPodcast.pay'),
          handler: () => {
            openPaymentPage();
          },
        },
      ],
    });

    await paymentAlert.present();
  },

  async addRecentlyPlayed({ state, commit }, { course, startAt }) {
    if (!course) {
      return;
    }

    const recentlyPlayedList = await getArrayItemFromLocalStorage(
      RECENTLY_PLAYED_STORAGE_KEY,
    );

    const stream = course?.streams?.[startAt];
    if (!stream) {
      return;
    }

    const storageData = state.recentlyPlayedProgress[stream?.id];
    const streamUserData = stream?.stream_user?.[0];

    const lastPosition =
      getLatestTrackPositionByTimestamp(streamUserData, storageData) ??
      '00:00:00';

    const recentlyPlayedStream = {
      course_title: course.course_title,
      course_id: course.id,
      course_media: course.media,
      stream_id: stream.id,
      id: stream.id,
      media: stream.media,
      stream_title: stream.title,
      duration: stream.duration,
      progress_end_time: lastPosition,
      progress_updated_timestamp: getCurrentUnix(),
      rss_feed_publish_date: stream.rss_feed_publish_date,
      created_at: stream.created_at,
    };

    const courseInRecentIndex = recentlyPlayedList.findIndex(
      (played) => played.stream_id === stream.id,
    );

    if (courseInRecentIndex > -1) {
      recentlyPlayedList.splice(courseInRecentIndex, 1);
      recentlyPlayedList.splice(
        recentlyPlayedList.length,
        0,
        recentlyPlayedStream,
      );
    } else {
      recentlyPlayedList.push(recentlyPlayedStream);
    }

    const recentListStringified = JSON.stringify(
      recentlyPlayedList.slice(-RECENTLY_PLAYED_MAX_COUNT),
    );

    await setKey(RECENTLY_PLAYED_STORAGE_KEY, recentListStringified);
    commit('setRecentlyPlayed', JSON.parse(recentListStringified));
  },

  async removeFromRecentlyPlayed({ commit }, streamId) {
    const recentlyPlayedList = await getArrayItemFromLocalStorage(
      RECENTLY_PLAYED_STORAGE_KEY,
    );
    const courseInRecentIndex = recentlyPlayedList.findIndex(
      (played) => played.stream_id === streamId,
    );

    if (courseInRecentIndex > -1) {
      recentlyPlayedList.splice(courseInRecentIndex, 1);
    }

    const recentListStringified = JSON.stringify(
      recentlyPlayedList.slice(-RECENTLY_PLAYED_MAX_COUNT),
    );

    await setKey(RECENTLY_PLAYED_STORAGE_KEY, recentListStringified);
    commit('setRecentlyPlayed', JSON.parse(recentListStringified));
  },

  closePlayer({ commit }) {
    commit('resetTrackInfo');
    commit('setPlaylist', { playlist: [] });
    commit('setCompanyData', null);
    commit('setPlayerState', false);
    commit('setPlayerCollapse', false);
  },

  async setRecentlyPlayedStorage({ commit }) {
    const recently = await getArrayItemFromLocalStorage(
      RECENTLY_PLAYED_STORAGE_KEY,
    );

    // @todo mutate with data from BE and replace it;

    commit('setRecentlyFromStorage', recently);
  },

  async loadRecentlyPlayedProgressStorage({ commit }) {
    const progress = await getObjectItemFromLocalStorage(
      RECENTLY_PLAYED_PROGRESS_STORAGE_KEY,
    );
    commit('setRecentlyPlayedProgress', progress);
  },

  async resetRecentlyPlayedProgressStorage({ commit, dispatch }) {
    commit('setRecentlyPlayedProgress', {});
    dispatch('setRecentlyPlayedProgressStorage');
  },

  updateRecentlyPlayedProgressStorage(
    { commit, dispatch },
    { stream, progress },
  ) {
    const timestamp = getCurrentUnix();
    commit('updateRecentlyPlayedProgress', { stream, progress, timestamp });
    dispatch('setRecentlyPlayedProgressStorage');
  },

  async setRecentlyPlayedProgressStorage({ commit, state }) {
    await setKey(
      RECENTLY_PLAYED_PROGRESS_STORAGE_KEY,
      JSON.stringify(state.recentlyPlayedProgress),
    );
  },

  getStreamUserProgress({ commit }, { streamId, userId }) {
    const url = STREAM_USER_PROGRESS.replace('{stream_id}', streamId).replace(
      '{user_id}',
      userId,
    );
    return new Promise((resolve, reject) => {
      axios
        .get(url)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  setListenedPercentage({ commit }, percent) {
    commit('setListenedPercentage', percent);
  },
};

export const mutations = {
  setPlayerState(state, value) {
    state.playerActive = value;
  },

  setPlayerCollapse(state, value) {
    state.playerCollapsed = value;
  },

  setEpisode(state, value) {
    state.currentEpisode = value;
  },

  setCompanyData(state, value) {
    state.currentCompany = value;
  },

  setCourseData(state, value) {
    state.currentCourse = value;
  },

  setSeriesData(state, value) {
    state.currentSeries = value;
  },

  seekStream(state, seconds) {
    state.seekPosition = seconds;
  },

  setRecentlyPlayed(state, data) {
    state.recentlyPlayed = data;
  },

  setRecentlyFromStorage(state, data) {
    state.recentlyPlayed = data;
  },

  setSkipAutoplayId(state, id) {
    state.streamSkipAutoplay = id;
  },

  setPlaylist(state, { playlist, startAt = 0 }) {
    if (Array.isArray(playlist)) {
      state.playlist = playlist;
      state.playlistTrackStart = startAt;
      state.playlistCurrentIndex = startAt;
    } else if (isObject(playlist)) {
      state.playlist = [playlist];
      state.playlistTrackStart = 0;
      state.playlistCurrentIndex = 0;
    }
  },

  setPlaylistCurrentIndex(state, index) {
    state.playlistCurrentIndex = index;
  },

  setRecentlyPlayedProgress(state, data) {
    state.recentlyPlayedProgress = data;
  },

  updateRecentlyPlayedProgress(state, { stream, progress, timestamp }) {
    state.recentlyPlayedProgress[stream] = { progress, timestamp };
  },

  setTrackInfo(state, data) {
    Object.assign(state.trackInfo, data);
  },

  resetTrackInfo(state) {
    state.trackInfo = {
      trackPlaying: false,
      trackDuration: 0,
      trackCurrentTime: 0,
    };
  },

  resetPlayerState(state) {
    Object.assign(state, initializeStates());
  },

  setProgressHistory(state, data) {
    state.progressHistory = data;
  },

  incrementPlayerPauseTrigger(state) {
    state.playerPlayPauseTriggers.pause += 1;
  },

  incrementPlayerPlayTrigger(state) {
    state.playerPlayPauseTriggers.play += 1;
  },

  setListenedPercentage(state, percent) {
    const floored = Math.floor(percent);
    state.percentageListened = Math.min(floored, 100);
  },
};

export const getters = {
  getCurrentEpisode: (state) => state.playlist[state.playlistCurrentIndex],
  getSeekPosition: (state) => state.seekPosition,
  getPlayerPlayPauseTriggers: (state) => state.playerPlayPauseTriggers,
  getPercantageListened: (state) => state.percentageListened,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
